import { Injectable } from "@angular/core";
import { MsalService } from "@azure/msal-angular";
import { environment } from "@env/environment";
import { TRUE } from "@shared/constants/general.constants";
import { ADVISOR_SSO_ACTIVE, ADVISOR_SSO_PENDING } from "@shared/constants/localstorage.constants";
import { ADVISOR_SSO_CTX } from "@shared/constants/msal.constants";

@Injectable({
  providedIn: "root",
})
export class MsalAuthenticationService {
  constructor(private readonly msalService: MsalService) {}

  isAdvisorSsoEntry(): boolean {
    const hasCtx = new URLSearchParams(window.location.search).has(ADVISOR_SSO_CTX);
    const hasPendingSso = sessionStorage.getItem(ADVISOR_SSO_PENDING) === TRUE;
    const hasActiveSso = sessionStorage.getItem(ADVISOR_SSO_ACTIVE) === TRUE;
    return this.isMsalEnabled() && (hasCtx || hasPendingSso || hasActiveSso);
  }

  isAdvisorSsoBootstrapFlow(): boolean {
    const hasCtx = new URLSearchParams(window.location.search).has(ADVISOR_SSO_CTX);
    const hasPendingSso = sessionStorage.getItem(ADVISOR_SSO_PENDING) === TRUE;
    return this.isMsalEnabled() && (hasCtx || hasPendingSso);
  }

  isRedirectInProgress(): boolean {
    return sessionStorage.getItem(ADVISOR_SSO_PENDING) === TRUE;
  }

  hasActiveSession(): boolean {
    let activeAccount = this.msalService.instance.getActiveAccount();
    const accounts = this.msalService.instance.getAllAccounts();

    if (!activeAccount && accounts.length > 0) {
      this.msalService.instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }

    return activeAccount !== null || accounts.length > 0;
  }

  async ensureAuthenticated(scopes: string[]): Promise<boolean> {
    if (this.isMsalAuthenticated()) {
      this.markActive();
      return true;
    }

    try {
      this.markPending();
      const targetUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      this.msalService.loginRedirect({ scopes, state: encodeURIComponent(targetUrl) });
      return false;
    } catch {
      this.clearFlags();
      return false;
    }
  }

  markPending(): void {
    sessionStorage.setItem(ADVISOR_SSO_PENDING, TRUE);
  }

  markActive(): void {
    sessionStorage.removeItem(ADVISOR_SSO_PENDING);
    sessionStorage.setItem(ADVISOR_SSO_ACTIVE, TRUE);
  }

  clearFlags(): void {
    sessionStorage.removeItem(ADVISOR_SSO_PENDING);
    sessionStorage.removeItem(ADVISOR_SSO_ACTIVE);
  }

  isMsalAuthenticated(): boolean {
    return this.hasActiveSession();
  }

  isMsalEnabled(): boolean {
    const enabled = environment.msalConfig?.enabled;
    return enabled.toLowerCase() === TRUE;
  }
}
